import { Reference } from '@approvalmax/types';
import { DropdownEditor, NumberEditor, TransferUpDownIcon } from '@approvalmax/ui';
import { Box, Flex, Text, Tooltip } from '@approvalmax/ui/src/components';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { constants, selectors } from '../../../../../../modules/common';
import { backend, domain } from '../../../../../../modules/data';
import { PlainDataProvider } from '../../../../../../modules/data-providers';
import { useSelector } from '../../../../../../modules/react-redux';
import { InfoIcon } from '../../../../../../modules/sprites';
import {
    changeQbooksApplyTaxAfterDiscount,
    changeQbooksDiscount,
    changeQbooksDiscountType,
    changeQbooksTaxCode,
} from '../../../../actions';
import { getRequest } from '../../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../../selectors/qbooks';
import { getTaxCodeList } from '../../../../selectors/qbooks/taxSelectors';
import { basedOnLocationTax, discountTypeReference } from './QBooksTaxSwitcherLegacy.constants';
import { findDiscountType } from './QBooksTaxSwitcherLegacy.helpers';
import { messages } from './QBooksTaxSwitcherLegacy.messages';
import { StyledButton, StyledGrid, TaxCodeContainer, TaxCodeTooltip } from './QBooksTaxSwitcherLegacy.styles';

const {
    qBooksConstants: { DISCOUNT_MIN, DISCOUNT_PERCENT_MAX, DISCOUNT_VALUE_MAX },
} = constants;

/**
 * @deprecated Use redesigned component {@link QBooksTaxSwitcher}
 */
export const QBooksTaxSwitcherLegacy = memo(() => {
    const dispatch = useDispatch();
    const request = useSelector(getRequest<domain.QBooksInvoiceRequest>);
    const qBooksContext = useSelector(getQBooksContext);
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const isUSOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;
    const {
        details: { discount, discountType, taxCode, applyTaxAfterDiscount, useAutoTaxes },
    } = request;
    const isInitialDiscountPresent = useRef(Boolean(discount));
    const isDiscountEnabled =
        qBooksContext.salesFormOptionalFields?.discountEnabled || isInitialDiscountPresent.current;

    const taxList = useMemo(() => {
        const taxCodeList = getTaxCodeList(qBooksContext, request.details.date, domain.TaxApplicableOnType.Sales);

        if (qBooksContext.isAutoTaxesEnabled) {
            return [basedOnLocationTax, ...taxCodeList];
        }

        return taxCodeList;
    }, [qBooksContext, request.details.date]);

    const handleDiscountChange = useCallback((value: number) => dispatch(changeQbooksDiscount(value)), [dispatch]);
    const handleDiscountTypeChange = useCallback(
        (type: { id: domain.QBooksDiscountType }) => dispatch(changeQbooksDiscountType(type.id)),
        [dispatch]
    );
    const handleTaxCodeChange = useCallback(
        (value: Reference | null) => dispatch(changeQbooksTaxCode(value)),
        [dispatch]
    );
    const handleAppleTaxAfterDiscountChange = useCallback(
        () => dispatch(changeQbooksApplyTaxAfterDiscount(!applyTaxAfterDiscount)),
        [dispatch, applyTaxAfterDiscount]
    );

    if (!isUSOrg && !isDiscountEnabled) {
        return null;
    }

    return (
        <Flex alignItems='center' spacing='0 12'>
            {isUSOrg && isDiscountEnabled && (
                <StyledButton theme='primary' colorTheme='white' rounded execute={handleAppleTaxAfterDiscountChange}>
                    <TransferUpDownIcon />
                </StyledButton>
            )}

            <StyledGrid
                gridTemplateColumns='max-content minmax(200px, auto)'
                rowGap={16}
                columnGap={12}
                alignItems='center'
                $reverse={applyTaxAfterDiscount}
            >
                {isUSOrg && (
                    <>
                        <Text font='label' color='midnight70'>
                            {messages.taxRateLabel}
                        </Text>

                        <TaxCodeContainer>
                            <PlainDataProvider items={taxList}>
                                <DropdownEditor
                                    value={useAutoTaxes ? basedOnLocationTax : taxCode}
                                    onChange={handleTaxCodeChange}
                                    placeholder={messages.taxRatePlaceholder}
                                />
                            </PlainDataProvider>

                            {useAutoTaxes && (
                                <Tooltip
                                    width={300}
                                    placement='top'
                                    activator={
                                        <TaxCodeTooltip spacing='4'>
                                            <InfoIcon width={10} height={10} color='midnight70' />
                                        </TaxCodeTooltip>
                                    }
                                >
                                    {messages.basedOnLocationTooltip}
                                </Tooltip>
                            )}
                        </TaxCodeContainer>
                    </>
                )}

                {isDiscountEnabled && (
                    <>
                        <Text font='label' color='midnight70'>
                            {messages.discountLabel}
                        </Text>

                        <Flex wrap={false} spacing='8'>
                            <PlainDataProvider items={discountTypeReference}>
                                <DropdownEditor
                                    value={findDiscountType(discountType)}
                                    onChange={handleDiscountTypeChange}
                                    buttonComponent={DropdownEditor.NoFilterButton}
                                />
                            </PlainDataProvider>

                            <Box width={100}>
                                <NumberEditor
                                    value={discount || null}
                                    onChange={handleDiscountChange}
                                    placeholder={messages.discountPlaceholder}
                                    precision={2}
                                    min={DISCOUNT_MIN}
                                    max={
                                        discountType === domain.QBooksDiscountType.Percent
                                            ? DISCOUNT_PERCENT_MAX
                                            : DISCOUNT_VALUE_MAX
                                    }
                                />
                            </Box>
                        </Flex>
                    </>
                )}
            </StyledGrid>
        </Flex>
    );
});

QBooksTaxSwitcherLegacy.displayName = 'QBooksTaxSwitcherLegacy';
